import {
  createTheme,
  ThemeOptions as MuiThemeOptions,
  Theme as MuiTheme,
} from '@mui/material/styles';
import {
  Palette as MuiPalette,
  PaletteOptions as MuiPaletteOptions,
} from '@mui/material/styles/createPalette';

export type CustomPalette = {
  textPrimaryBlack: string;
  textPrimaryWhite: string;
  textSecondaryDarkGrey: string;
  textWarning: string;
  secDefault: string;
  primary500: string;
  green50: string;
  green400: string;
  green500: string;
  green700: string;
  textGray: string;
  grey50: string;
  grey100: string;
  grey200: string;
  grey300: string;
  grey400: string;
  grey500: string;
  grey600: string;
  grey800: string;
  grey900: string;
  blue50: string;
  blue500: string;
  black: string;
  black80o: string;
  black40o: string;
  white: string;
  red400: string;
  iconBlack: string;
  iconGrey: string;
  red50: string;
  red300: string;
  red500: string;
  red600: string;
  yellow50: string;
  yellow500: string;
  orange500: string;
  orange50: string;
};

interface CustomTheme {
  radii: Record<'xxs' | 'xs' | 'sm' | 'md', number>;
  zIndexes: Record<'resourcesSidebar' | 'downloadManager', number>;
}

declare module '@mui/material/styles/createPalette' {
  // eslint-disable-next-line
  // @ts-ignore
  interface Palette extends MuiPalette, CustomPalette {}
  // @ts-expect-error PaletteOptions recursively references itself
  interface PaletteOptions extends MuiPaletteOptions, CustomPalette {}
}

declare module '@mui/material/styles' {
  // @ts-expect-error Theme recursively references itself
  interface Theme extends MuiTheme, CustomTheme {}
  // @ts-expect-error ThemeOptions recursively references itself
  interface ThemeOptions extends MuiThemeOptions, CustomTheme {}
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#BF1220',
    },
    textPrimaryBlack: '#131927',
    textPrimaryWhite: '#fff',
    primary500: '#4E61F6',
    green50: '#F0FDF4',
    green400: '#69C57D',
    green500: '#43B75D',
    green700: '#308242',
    textGray: '#9EA2AE',
    grey50: '#F9FAFB',
    grey100: '#F3F4F6',
    grey200: '#E5E7EA',
    grey300: '#D2D5DB',
    grey400: '#9EA2AE',
    grey500: '#6D717F',
    grey600: '#4D5461',
    grey800: '#212936',
    grey900: '#131927',
    blue50: '#B3E0FF88',
    blue500: '#0095FF',
    black: '#000',
    black80o: '#000000CC',
    black40o: '#00000066',
    white: '#fff',
    red400: '#F16965',
    iconBlack: '#0A0B0EFF',
    iconGrey: '#9EA2AE',
    textSecondaryDarkGrey: '#6D717F',
    textWarning: '#FFAA00',
    secDefault: '#6D6D6D',
    red50: '#FDECEC',
    red300: '#F4827E',
    red500: '#EE443F',
    red600: '#BF1220',
    yellow50: '#FFF7E6',
    yellow500: '#FA0',
    orange50: 'rgba(251, 146, 60, 0.1)',
    orange500: 'rgb(251, 146, 60)',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          height: '100%',
        },
        body: {
          height: '100%',
        },
        '#root': {
          display: 'flex',
          height: '100%',
          flexDirection: 'column',
        },
        a: {
          cursor: 'pointer',
          textDecoration: 'underline',
          color: '#BF1220',
        },
        '*': {
          '::-webkit-scrollbar': {
            width: '4px',
            height: '4px',
          },
          '::-webkit-scrollbar-track': {
            background: '#E5E7EA',
            borderRadius: '2px',
          },
          '::-webkit-scrollbar-thumb': {
            background: '#9EA2AE',
            borderRadius: '2px',
          },
          '::-webkit-scrollbar-thumb:hover': {
            background: '#7A7D87FF',
          },
        },
        '*.dark-scrollbar': {
          '::-webkit-scrollbar-track': {
            background: '#9EA2AE',
          },
          '::-webkit-scrollbar-thumb': {
            background: '#E5E7EA',
          },
          '::-webkit-scrollbar-thumb:hover': {
            background: '#BBC0CEFF',
          },
        },
        'body, *.wide-scrollbar': {
          '::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '::-webkit-scrollbar-thumb': {
            borderRadius: '3px',
          },
        },
        '.page-width-wrapper': {
          maxWidth: 1262,
          margin: '0 auto',
          width: '100%',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          boxShadow: '0px 4px 4px 0px #0000001A',
        },
      },
    },
  },
  radii: {
    xxs: 4,
    xs: 8,
    sm: 12,
    md: 16,
  },
  zIndexes: {
    resourcesSidebar: 1000,
    downloadManager: 1100,
  },
});

export default theme;
